import type { MailboxInfo } from "owa-client-types";
import type { FolderForestNode, FolderForestNodeType, GroupForestNode } from "owa-favorites-types";
import { getFolderIdForSelectedNode, getSelectedNode } from "owa-mail-folder-forest-store";
import React from "react";

export interface RetainedFolderForFavoritesSideRail {
    // Undefined for nodes without a backing folder (e.g. a persona whose search folder is not populated).
    folderId: string | undefined;
    nodeId: string;
    treeType: FolderForestNode["treeType"];
    nodeType: FolderForestNodeType;
    mailboxInfo?: MailboxInfo;
}

export default function useRetainedFolderForFavoritesSideRail(isSideRailVisible: boolean, isFolderPaneOverlayOpen: boolean): RetainedFolderForFavoritesSideRail | undefined {
    const retainedFolderRef = React.useRef<RetainedFolderForFavoritesSideRail | undefined>(isSideRailVisible ? getSelectedFolder() : undefined);
    const wasSideRailVisibleRef = React.useRef(isSideRailVisible);
    const wasFolderPaneOverlayOpenRef = React.useRef(isFolderPaneOverlayOpen);
    const isBecomingVisible = isSideRailVisible && !wasSideRailVisibleRef.current;
    // The rail stays visible while the folder pane overlay is open, so treat the overlay
    // closing like the rail becoming visible to pick up a folder selected in the overlay.
    const isOverlayClosing = isSideRailVisible && wasFolderPaneOverlayOpenRef.current && !isFolderPaneOverlayOpen;
    const retainedFolder = !isSideRailVisible
        ? undefined
        : isBecomingVisible || isOverlayClosing
            ? getSelectedFolder()
            : retainedFolderRef.current;
    
    React.useEffect(() => {
        retainedFolderRef.current = isSideRailVisible ? retainedFolder : undefined;
        wasSideRailVisibleRef.current = isSideRailVisible;
        wasFolderPaneOverlayOpenRef.current = isFolderPaneOverlayOpen;
    }, [isSideRailVisible, isFolderPaneOverlayOpen, retainedFolder]);
    
    return retainedFolder;
}

function getSelectedFolder(): RetainedFolderForFavoritesSideRail | undefined {
    const selectedNode = getSelectedNode();
    if (!selectedNode?.id) {
        return undefined;
    }
    
    const retained: RetainedFolderForFavoritesSideRail = {
        folderId: getFolderIdForSelectedNode() || undefined,
        nodeId: selectedNode.id,
        treeType: selectedNode.treeType,
        nodeType: selectedNode.type,
    };
    const mailboxInfo = (selectedNode as Partial<GroupForestNode>).mailboxInfo;
    if (mailboxInfo) {
        retained.mailboxInfo = mailboxInfo;
    }
    return retained;
}
